import React, { useState } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Power, Trophy, ClockCounterClockwise, Key, List, X } from "@phosphor-icons/react";

const NAV = [
  { to: "/admin", label: "Result", Icon: Trophy, end: true },
  { to: "/admin/previous", label: "Previous Result", Icon: ClockCounterClockwise },
  { to: "/admin/password", label: "Change Password", Icon: Key },
];

const TITLES = {
  "/admin": "Result",
  "/admin/previous": "Previous Result",
  "/admin/password": "Change Password",
};

export function AdminLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const title = TITLES[location.pathname] || "Admin";

  const doLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const NavItems = ({ onClick }) =>
    NAV.map(({ to, label, Icon, end }) => (
      <NavLink
        key={to}
        to={to}
        end={end}
        onClick={onClick}
        data-testid={`admin-nav-${label.toLowerCase().replace(/\s+|\//g, "-")}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3.5 rounded-xl font-semibold transition-colors ${
            isActive ? "bg-brand text-white shadow-md" : "text-slate-600 hover:bg-slate-100"
          }`
        }
      >
        <Icon className="w-5 h-5" weight="duotone" />
        {label}
      </NavLink>
    ));

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col md:flex-row">
      {/* Mobile top bar: hamburger | title | red Log Out */}
      <div className="md:hidden sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between pl-2 pr-0 h-16">
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen((o) => !o)} data-testid="admin-mobile-toggle" className="w-11 h-11 flex items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100">
            {open ? <X className="w-7 h-7" /> : <List className="w-7 h-7" />}
          </button>
          <span className="font-heading font-bold text-2xl text-slate-500">{title}</span>
        </div>
        <button onClick={doLogout} data-testid="admin-logout-mobile" className="h-16 px-5 bg-red-500 text-white font-bold text-lg flex items-center gap-2 hover:bg-red-600 transition-colors">
          <Power className="w-6 h-6" weight="bold" /> Log Out
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-b border-slate-200 p-3 space-y-1 animate-fade-in">
          <NavItems onClick={() => setOpen(false)} />
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 bg-white border-r border-slate-200 min-h-screen p-4 sticky top-0">
        <div className="flex items-center gap-2.5 px-2 py-3 mb-4">
          <img src="/logo.png" alt="logo" className="w-10 h-10 rounded-xl object-cover" />
          <div className="leading-tight">
            <p className="font-heading font-extrabold text-sm text-slate-900">Guwahati Morning</p>
            <p className="font-heading font-extrabold text-sm text-brand tracking-widest">TEER ADMIN</p>
          </div>
        </div>
        <nav className="space-y-1.5 flex-1">
          <NavItems />
        </nav>
        <button onClick={doLogout} data-testid="admin-logout-desktop" className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 transition-colors">
          <Power className="w-5 h-5" weight="bold" /> Log Out
        </button>
      </aside>

      <main className="flex-1 p-4 sm:p-6 md:p-8 max-w-3xl w-full mx-auto">
        <Outlet />
      </main>
    </div>
  );
}
